import { API_BASE_CLIENT as API_BASE, STORAGE_KEYS, isCapacitorRuntime } from "../config/vars";

function getMobileAccessToken() {
    if (!isCapacitorRuntime()) return null;
    try {
        return localStorage.getItem(STORAGE_KEYS.MOBILE_ACCESS_TOKEN);
    } catch (_) {
        return null;
    }
}

function getMobileRefreshToken() {
    if (!isCapacitorRuntime()) return null;
    try {
        return localStorage.getItem(STORAGE_KEYS.MOBILE_REFRESH_TOKEN);
    } catch (_) {
        return null;
    }
}

function storeMobileTokens({ accessToken, refreshToken } = {}) {
    try {
        if (accessToken) {
            localStorage.setItem(STORAGE_KEYS.MOBILE_ACCESS_TOKEN, accessToken);
        }
        if (refreshToken) {
            localStorage.setItem(STORAGE_KEYS.MOBILE_REFRESH_TOKEN, refreshToken);
        }
    } catch (_) {
        // ignore
    }
}

function _getPath(input) {
    if (typeof input !== "string") return "";
    try {
        return new URL(input, window.location.origin).pathname;
    } catch (_) {
        return input;
    }
}

function shouldSkipRefresh(input) {
    const path = _getPath(input);
    if (!path.includes("/login/")) return false;

    // Avoid refresh loops or unnecessary refresh attempts.
    if (path.endsWith("/login/refresh")) return true;
    if (path.endsWith("/login/logout")) return true;

    // Skip refresh for actual login endpoints (but NOT /login/me).
    return /\/login\/(admin|operator|client)(\/verify-code)?$/.test(path);
}

function shouldSkipClientContextHeader(input) {
    const path = _getPath(input);
    return path.includes("/login/");
}

let refreshInFlight = null;

async function refreshMobileSession() {
    if (!API_BASE) return false;
    if (refreshInFlight) return refreshInFlight;

    refreshInFlight = (async () => {
        const refreshToken = getMobileRefreshToken();
        const headers = {
            "X-Return-Token": "1",
        };
        if (refreshToken) {
            headers["X-Refresh-Token"] = refreshToken;
        }

        const refreshRes = await fetch(`${API_BASE}/login/refresh`, {
            method: "POST",
            credentials: "include",
            headers,
        });
        if (!refreshRes.ok) return false;

        let data = null;
        try {
            data = await refreshRes.json();
        } catch (_) {
            data = null;
        }

        const nextAccess = (data?.access_token || "").trim();
        const nextRefresh = (data?.refresh_token || "").trim();
        if (!nextAccess) return false;

        storeMobileTokens({
            accessToken: nextAccess,
            refreshToken: nextRefresh || refreshToken,
        });
        return true;
    })();

    try {
        return await refreshInFlight;
    } finally {
        refreshInFlight = null;
    }
}

export async function apiFetch(input, init = {}, options = {}) {
    const { retryOn401 = true } = options;

    const mobileToken = getMobileAccessToken();
    const headers = new Headers(init.headers || {});
    try {
        const activeContextId = localStorage.getItem(STORAGE_KEYS.ACTIVE_CLIENT_CONTEXT_ID);
        if (activeContextId && !headers.has("X-Client-Context-Id") && !shouldSkipClientContextHeader(input)) {
            headers.set("X-Client-Context-Id", activeContextId);
        }
    } catch (_) {
        // ignore
    }
    if (mobileToken && !headers.has("Authorization")) {
        headers.set("Authorization", `Bearer ${mobileToken}`);
    }

    const res = await fetch(input, {
        ...init,
        headers,
        credentials: "include",
    });

    // License enforcement: backend returns 402 when license is missing/expired.
    // Emit a global event so the app can show an informative banner/screen.
    if (res.status === 402) {
        try {
            window.dispatchEvent(new CustomEvent("licenseBlocked"));
        } catch (_) {
            // ignore
        }
    }

    if (!retryOn401 || res.status !== 401 || shouldSkipRefresh(input) || !API_BASE) {
        return res;
    }

    // Web cookie session
    if (!mobileToken) {
        const refreshRes = await fetch(`${API_BASE}/login/refresh`, {
            method: "POST",
            credentials: "include",
        });

        if (!refreshRes.ok) {
            return res;
        }

        return fetch(input, {
            ...init,
            credentials: "include",
        });
    }

    // Capacitor Bearer fallback: refresh via stored refresh token (cookies often unavailable).
    const refreshed = await refreshMobileSession();
    if (!refreshed) {
        return res;
    }

    const retryHeaders = new Headers(init.headers || {});
    try {
        const activeContextId = localStorage.getItem(STORAGE_KEYS.ACTIVE_CLIENT_CONTEXT_ID);
        if (activeContextId && !retryHeaders.has("X-Client-Context-Id") && !shouldSkipClientContextHeader(input)) {
            retryHeaders.set("X-Client-Context-Id", activeContextId);
        }
    } catch (_) {
        // ignore
    }
    const nextAccess = getMobileAccessToken();
    if (nextAccess) {
        retryHeaders.set("Authorization", `Bearer ${nextAccess}`);
    }

    return fetch(input, {
        ...init,
        headers: retryHeaders,
        credentials: "include",
    });
}
